import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { MapPin, ChevronDown, Facebook, Instagram, Menu, User, LogOut } from 'lucide-react';
import logoVagas from '../img/logo-capa.png';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { settingsService } from '../services/settings.service';
import { authService } from '../services';

// Link do painel externo (configurável via env para homologação)
const PAINEL_URL = import.meta.env.VITE_PAINEL_URL || 'https://maisqvagas.h2k.com.br';

export default function Header() {
  const [facebookUrl, setFacebookUrl] = useState<string>('https://www.facebook.com/groups/vagasbrusque/?locale=pt_BR');
  const [instagramUrl, setInstagramUrl] = useState<string>('https://www.instagram.com/vagasbrusque.com.br?igsh=cHY1eGxvajhjaWxt');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [userType, setUserType] = useState<string | null>(null);

  useEffect(() => {
    // Verifica auth no localStorage (token ou sessão já detectada)
    const authenticated = authService.isAuthenticated();
    if (authenticated) {
      setIsLoggedIn(true);
      const user = authService.getUser();
      if (user) setUserName(user.name || '');
      setUserType(authService.getUserType());
      return;
    }

    // Sem token — tenta detectar sessão do backend via cookie frontend_user
    const user = authService.checkSession();
    if (user) {
      setIsLoggedIn(true);
      setUserName(user.name || '');
      setUserType(authService.getUserType() || (user as any).type || 'candidate');
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const s = await settingsService.getPublicSettings();
        if (s.facebook_url) setFacebookUrl(s.facebook_url);
        if (s.instagram_url) setInstagramUrl(s.instagram_url);
      } catch {
        // ignore
      }
    })();
  }, []);

  const handleLogout = () => {
    // Limpa localStorage e cookie
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_session');
    localStorage.removeItem('user');
    localStorage.removeItem('user_type');
    document.cookie = 'frontend_user=; path=/; domain=.h2k.com.br; max-age=0';
    setIsLoggedIn(false);
    setUserName('');
    setUserType(null);
    // Redireciona para logout do backend (encerra sessão Livewire)
    window.location.href = `${PAINEL_URL}/logout`;
  };

  const dashboardUrl = userType === 'company'
    ? `${PAINEL_URL}/empresa/dashboard`
    : userType === 'admin'
      ? `${PAINEL_URL}/admin/dashboard`
      : `${PAINEL_URL}/candidato/dashboard`;

  return (
    <header className="bg-[#87ca1c] border-b sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={logoVagas} alt="Vagas Brusque" className="h-28" />
          </Link>

          {/* Seletor de Cidade (desktop) */}
          <div className="hidden md:block">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 ml-10 text-white">
                  <MapPin className="size-4" />
                  <span>Brusque</span>
                  <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem>
                  <MapPin className="size-4 mr-2" />
                  Brusque
                </DropdownMenuItem>
                <DropdownMenuItem disabled className="text-gray-400">
                  <MapPin className="size-4 mr-2" />
                  Itajaí (em breve)
                </DropdownMenuItem>
                <DropdownMenuItem disabled className="text-gray-400">
                  <MapPin className="size-4 mr-2" />
                  Blumenau (em breve)
                </DropdownMenuItem>
                <DropdownMenuItem disabled className="text-gray-400">
                  <MapPin className="size-4 mr-2" />
                  Porto Alegre (em breve)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Spacer para empurrar o menu e ações para a direita */}
          <div className="flex-1" />

          {/* Menu e Ações */}
          <div className="flex items-center gap-2 md:gap-6">
            {/* Links de navegação */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/vagas" className="text-white hover:text-brand-900 transition-colors">
                Vagas
              </Link>
              <Link to="/contato" className="text-white hover:text-brand-900 transition-colors">
                Contato
              </Link>
            </nav>

            {/* Social icons */}
            <div className="hidden md:flex items-center gap-3">
              <a
                href={facebookUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="bg-white p-2 rounded-full transition-colors text-[#1877F2] hover:bg-[#6CA7F5]"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={instagramUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="bg-white p-2 rounded-full transition-colors text-[#E4405F] hover:bg-[#ED728B]"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>

            {/* Ações (desktop) */}
            <div className="hidden md:flex items-center gap-3">
              {isLoggedIn ? (
                <>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="gap-2 text-white">
                        <User className="size-4" />
                        <span className="max-w-[120px] truncate">{userName || 'Minha conta'}</span>
                        <ChevronDown className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => { window.location.href = dashboardUrl; }}>
                        Painel
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={handleLogout} className="text-red-600">
                        <LogOut className="size-4 mr-2" />
                        Sair
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {userType === 'company' && (
                    <a href={`${PAINEL_URL}/empresa/vaga/nova`} className="outline-none focus-visible:outline-none">
                      <Button className="btn-led-pulse-subtle">Anunciar vaga</Button>
                    </a>
                  )}
                </>
              ) : (
                <>
                  <a href={`${PAINEL_URL}/login?redirect=${encodeURIComponent(window.location.href)}`}>
                    <Button variant="ghost" className="text-white">Login</Button>
                  </a>
                  <a href={`${PAINEL_URL}/login?redirect=${encodeURIComponent(window.location.href)}`} className="outline-none focus-visible:outline-none">
                    <Button className="btn-led-pulse-subtle">Anunciar vaga</Button>
                  </a>
                </>
              )}
            </div>

            {/* Hamburguer (mobile) */}
            <div className="md:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Abrir menu">
                    <Menu className="size-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <SheetHeader>
                    <SheetTitle className="text-brand-600 text-xl font-bold">Menu</SheetTitle>
                  </SheetHeader>
                  <div className="mt-4 space-y-2">
                    {/* Navegação principal */}
                    <div className="px-4">
                      <Link to="/vagas">
                        <Button variant="ghost" className="w-full justify-start h-12 text-base">Vagas</Button>
                      </Link>
                      <Link to="/como-funciona">
                        <Button variant="ghost" className="w-full justify-start h-12 text-base">Como funciona</Button>
                      </Link>
                      <Link to="/sobre">
                        <Button variant="ghost" className="w-full justify-start h-12 text-base">Sobre</Button>
                      </Link>
                      <Link to="/contato">
                        <Button variant="ghost" className="w-full justify-start h-12 text-base">Contato</Button>
                      </Link>
                    </div>

                    <div className="pt-2 mt-2 border-t px-4 flex flex-col gap-2">
                      <div className="text-xs text-gray-500 mb-1">Cidade</div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="w-full justify-start gap-2">
                            <MapPin className="size-4" /> Brusque
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem><MapPin className="size-4 mr-2" /> Brusque</DropdownMenuItem>
                          <DropdownMenuItem disabled className="text-gray-400"><MapPin className="size-4 mr-2" /> Itajaí (em breve)</DropdownMenuItem>
                          <DropdownMenuItem disabled className="text-gray-400"><MapPin className="size-4 mr-2" /> Blumenau (em breve)</DropdownMenuItem>
                          <DropdownMenuItem disabled className="text-gray-400"><MapPin className="size-4 mr-2" /> Porto Alegre (em breve)</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="pt-2 mt-2 border-t px-4">
                      {isLoggedIn ? (
                        <>
                          <a href={dashboardUrl}>
                            <Button size="lg" className="w-full bg-brand-600 hover:bg-brand-700 text-white">
                              <User className="size-4 mr-2" />
                              {userName || 'Minha conta'}
                            </Button>
                          </a>
                          {userType === 'company' && (
                            <a href={`${PAINEL_URL}/empresa/vaga/nova`}>
                              <Button className="w-full mt-2 btn-led-pulse-subtle">Anunciar vaga</Button>
                            </a>
                          )}
                          <Button
                            variant="outline"
                            className="w-full mt-2 text-red-600 border-red-200"
                            onClick={handleLogout}
                          >
                            <LogOut className="size-4 mr-2" />
                            Sair
                          </Button>
                        </>
                      ) : (
                        <>
                          <a href={`${PAINEL_URL}/login?redirect=${encodeURIComponent(window.location.href)}`}>
                            <Button size="lg" className="w-full bg-brand-600 hover:bg-brand-700 text-white">Login</Button>
                          </a>
                          <a href={`${PAINEL_URL}/login?redirect=${encodeURIComponent(window.location.href)}`}>
                            <Button className="w-full mt-2 btn-led-pulse-subtle">Anunciar vaga</Button>
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
